/**
 * futuresFamilies — futures-instrument family lookup.
 *
 * Used by behavioral analyzers (currently thesisValidation) to treat
 * trades on related futures contracts as "the same instrument" for the
 * purpose of drift detection.
 *
 * Concrete motivating case (2026-05-18): a trader who runs MNQ on one
 * account and MES on another in parallel shouldn't be flagged for
 * "off-thesis (symbol)" drift when the thesis says one and the trades
 * are on the other — the two indices co-move closely enough that
 * counting it as a discipline leak is wrong.
 *
 * The model is two-tier:
 *
 *   1. STRICT FAMILY — micro/mini pair sharing the SAME underlying.
 *      `MES ↔ ES` (S&P 500 futures), `MNQ ↔ NQ` (Nasdaq 100 futures),
 *      `GC ↔ MGC` (gold), `CL ↔ MCL` (crude), etc. These are literally
 *      the same instrument at different contract sizes — never a drift
 *      to flip between them.
 *
 *   2. CROSS-INDEX CORRELATION GROUP — broader "all major US index
 *      futures move together enough that a trader couldn't reasonably
 *      be said to have drifted." Includes S&P, Nasdaq, Russell, Dow
 *      families. The user explicitly opted into this: "MNQ and MES
 *      travel in parallel much of the time."
 *
 *      Callers can opt OUT of cross-index matching via the
 *      `allowCrossIndex: false` flag if they want stricter semantics.
 *
 * Symbols not in the map have NO family and only match themselves
 * (i.e. extending the catalog requires an explicit entry, no implicit
 * suffix-stripping).
 *
 * Pure module — no Electron, React, or filesystem dependencies. Safe
 * to import from analyzers, hooks, or UI components.
 */

// Family key strings are arbitrary; they only need to be stable and
// unique per family. Lowercase + underscored is the convention.
const FUTURES_FAMILIES = Object.freeze({
  // ── US Equity Index Futures ────────────────────────────────────
  // S&P 500
  ES:  'sp500',
  MES: 'sp500',
  // Nasdaq 100
  NQ:  'nasdaq',
  MNQ: 'nasdaq',
  // Russell 2000
  RTY: 'russell',
  M2K: 'russell',
  // Dow Jones Industrial
  YM:  'dow',
  MYM: 'dow',

  // ── Metals ─────────────────────────────────────────────────────
  GC:  'gold',
  MGC: 'gold',
  SI:  'silver',
  SIL: 'silver',     // micro silver (CME) — appears as both 'SIL' and 'MSI' depending on export
  HG:  'copper',
  MHG: 'copper',
  PL:  'platinum',
  PA:  'palladium',

  // ── Energy ─────────────────────────────────────────────────────
  CL:  'crude',
  MCL: 'crude',
  QM:  'crude',      // legacy mini crude
  NG:  'natgas',
  QG:  'natgas',
  RB:  'rbob',       // gasoline
  HO:  'heating',

  // ── Treasury / Bonds ───────────────────────────────────────────
  ZB:  'bonds_us30',
  UB:  'bonds_ultra',
  ZN:  'bonds_us10',
  TN:  'bonds_ultra10',
  ZF:  'bonds_us5',
  ZT:  'bonds_us2',

  // ── FX (e-mini + micro pairings) ───────────────────────────────
  '6E': 'fx_euro',  M6E: 'fx_euro',
  '6B': 'fx_gbp',   M6B: 'fx_gbp',
  '6J': 'fx_jpy',   M6J: 'fx_jpy',
  '6A': 'fx_aud',   M6A: 'fx_aud',
  '6C': 'fx_cad',   M6C: 'fx_cad',
  '6S': 'fx_chf',
  '6N': 'fx_nzd',
  DX:  'fx_dxy',

  // ── Crypto ─────────────────────────────────────────────────────
  BTC: 'btc',
  MBT: 'btc',
  ETH: 'eth',
  MET: 'eth',

  // ── Agricultural / Softs ───────────────────────────────────────
  ZC:  'corn',
  ZS:  'soybeans',
  ZW:  'wheat',
  KE:  'wheat',      // hard red winter wheat — same complex
  ZL:  'soyoil',
  ZM:  'soymeal',
});

// The broader "indices travel together" correlation group. Members are
// FAMILY keys (not symbols). When `allowCrossIndex` is true (the default
// for behavioral analyzers like thesisValidation), any two symbols whose
// family keys both appear here count as same-family for matching purposes.
//
// Tradeoff: this is permissive. NQ can rip while ES yawns on a tech-
// specific catalyst — calling those "the same instrument" overstates the
// correlation. But the user case the analyzer needs to handle is
// "I take the same trade idea on multiple correlated instruments", and
// the false-positive cost of NOT including this group is much higher
// (every cross-index trade flagged as drift).
const INDEX_CORRELATION_GROUP = Object.freeze(new Set([
  'sp500', 'nasdaq', 'russell', 'dow',
]));

/**
 * Look up the family key for a symbol. Returns the family string
 * (e.g. 'sp500') or null if the symbol isn't in our catalog.
 *
 * Input is upper-cased before lookup, so 'mnq', 'MNQ', and 'Mnq' all
 * resolve identically.
 */
export const futuresFamily = (symbol) => {
  if (!symbol || typeof symbol !== 'string') return null;
  return FUTURES_FAMILIES[symbol.toUpperCase()] || null;
};

/**
 * Are two symbols members of the same futures family?
 *
 * @param {string} a
 * @param {string} b
 * @param {Object} [options]
 * @param {boolean} [options.allowCrossIndex=true]
 *   When true (default), two symbols whose families both belong to the
 *   INDEX_CORRELATION_GROUP also count as same-family. Set to false for
 *   strict "literally the same underlying" semantics.
 * @returns {boolean}
 */
export const sameFuturesFamily = (a, b, { allowCrossIndex = true } = {}) => {
  const fa = futuresFamily(a);
  const fb = futuresFamily(b);
  if (!fa || !fb) return false;
  if (fa === fb) return true;
  if (allowCrossIndex && INDEX_CORRELATION_GROUP.has(fa) && INDEX_CORRELATION_GROUP.has(fb)) {
    return true;
  }
  return false;
};

// Exports for tests + advanced introspection
export { FUTURES_FAMILIES, INDEX_CORRELATION_GROUP };
